/**
 * Link normalization — converts FS/SS/FF/SF links into normalized FS-shape
 * scheduling edges, with per-component calendar tagging per D-10.
 *
 * Ported from `gantt/sources/core/relations/links_common.js`
 * `_convertToFinishToStartLink(id, link, sourceTask, targetTask, ...)` at
 * lines 401–441.
 *
 * Key difference from v1: v1 collapsed all three duration components
 * (user lag, source-duration adjustment, target-duration adjustment) into
 * a single scalar `lag` interpreted in a single calendar. That's the
 * mixed-calendar drift bug described in `04-` Part I §2. v2 keeps the three
 * components separate, each tagged with its own calendar. The scheduling
 * pass (Phase C) evaluates each in its native calendar at the point of use.
 *
 * Per `04-` §15 resolution rules:
 *   - lagCalendar       = target task's calendar
 *   - sourceLagCalendar = source task's calendar
 *   - targetLagCalendar = target task's calendar
 *
 * The function produces a SchedEdge for the scheduling graph. The analysis
 * graph uses an analogous helper (see `normalizeLinkForAnalysis`) that
 * produces AnalysisEdge — same structure with `originalType` retained on the
 * edge as `type` (analysis preserves the original link type for slack-arithmetic
 * purposes; scheduling collapses to FS+lags).
 */

import type {
  CalendarRegistry,
  LinkSnapshot,
  TaskSnapshot
} from "../types";
import type { SchedEdge, AnalysisEdge } from "./types";

/**
 * Convert a LinkSnapshot to a SchedEdge with normalized FS-shape components
 * and per-component calendars.
 *
 * Behavior matrix (matches v1 _convertToFinishToStartLink):
 *
 *   FS: lag = link.lag,                                    sourceLag = 0,                 targetLag = 0
 *   SS: lag = link.lag,                                    sourceLag = -source.duration,  targetLag = 0
 *   FF: lag = link.lag,                                    sourceLag = 0,                 targetLag = -target.duration
 *   SF: lag = link.lag,                                    sourceLag = -source.duration,  targetLag = -target.duration
 *
 * v1 then summed source/target-lag into `lag` to produce a single scalar.
 * v2 does NOT — each component lives on its own and is interpreted in its
 * own calendar.
 */
export function normalizeLink(
  link: LinkSnapshot,
  sourceTask: TaskSnapshot,
  targetTask: TaskSnapshot,
  calendars: CalendarRegistry
): SchedEdge {
  const lagCalendar = calendars.resolveForTask(targetTask.id);
  const sourceLagCalendar = calendars.resolveForTask(sourceTask.id);
  const targetLagCalendar = calendars.resolveForTask(targetTask.id);

  let sourceLag = 0;
  let targetLag = 0;
  switch (link.type) {
    case "fs":
      // No duration adjustment.
      break;
    case "ss":
      sourceLag = -sourceTask.duration;
      break;
    case "ff":
      targetLag = -targetTask.duration;
      break;
    case "sf":
      sourceLag = -sourceTask.duration;
      targetLag = -targetTask.duration;
      break;
    default: {
      // Exhaustiveness check — LinkType is a closed union.
      const _exhaustive: never = link.type;
      void _exhaustive;
    }
  }

  return {
    linkId: link.id,
    source: link.source,
    target: link.target,
    originalType: link.type,

    lag: link.lag,
    lagUnit: link.lagUnit,
    lagCalendar,

    sourceLag,
    sourceLagUnit: sourceTask.durationUnit,
    sourceLagCalendar,

    targetLag,
    targetLagUnit: targetTask.durationUnit,
    targetLagCalendar
  };
}

/**
 * Same normalization for the analysis graph. Differs from scheduling by
 * keeping the original `type` field (for slack arithmetic that depends on
 * link type direction) instead of `originalType`.
 *
 * Per D-13/D-16, the analysis graph runs independently of scheduling-mode
 * config — but its calendar resolution is the same as scheduling per §15.
 */
export function normalizeLinkForAnalysis(
  link: LinkSnapshot,
  sourceTask: TaskSnapshot,
  targetTask: TaskSnapshot,
  calendars: CalendarRegistry
): AnalysisEdge {
  const lagCalendar = calendars.resolveForTask(targetTask.id);
  const sourceLagCalendar = calendars.resolveForTask(sourceTask.id);
  const targetLagCalendar = calendars.resolveForTask(targetTask.id);

  let sourceLag = 0;
  let targetLag = 0;
  switch (link.type) {
    case "fs":
      break;
    case "ss":
      sourceLag = -sourceTask.duration;
      break;
    case "ff":
      targetLag = -targetTask.duration;
      break;
    case "sf":
      sourceLag = -sourceTask.duration;
      targetLag = -targetTask.duration;
      break;
    default: {
      const _exhaustive: never = link.type;
      void _exhaustive;
    }
  }

  return {
    linkId: link.id,
    source: link.source,
    target: link.target,
    type: link.type,

    lag: link.lag,
    lagUnit: link.lagUnit,
    lagCalendar,

    sourceLag,
    sourceLagUnit: sourceTask.durationUnit,
    sourceLagCalendar,

    targetLag,
    targetLagUnit: targetTask.durationUnit,
    targetLagCalendar
  };
}
